import React, { useState, useEffect } from 'react';
import { RotateCw, FileText, Download } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, PDFPreview, ToolActionButtons } from '../shared';
import { PDFDocument, degrees } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

const RotatePDF = () => {
    const [file, setFile] = useState(null);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [totalPages, setTotalPages] = useState(0);
    const [pageRotations, setPageRotations] = useState({});
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewFilename, setPreviewFilename] = useState('');
    const [showPreview, setShowPreview] = useState(false);

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);

            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const pdf = await PDFDocument.load(arrayBuffer);
                setPdfDoc(pdf);
                setTotalPages(pdf.getPageCount());

                // Initialize rotations to 0 for all pages
                const initialRotations = {};
                for (let i = 0; i < pdf.getPageCount(); i++) {
                    initialRotations[i] = 0;
                }
                setPageRotations(initialRotations);

                toast.success(`PDF loaded: ${pdf.getPageCount()} pages`);
            } catch (error) {
                toast.error(`Failed to load PDF: ${error.message}`);
            }
        }
    };

    const rotatePage = (pageIndex, angle) => {
        setPageRotations(prev => ({
            ...prev,
            [pageIndex]: (prev[pageIndex] + angle) % 360
        }));
    };

    const rotateAllPages = (angle) => {
        const newRotations = {};
        for (let i = 0; i < totalPages; i++) {
            newRotations[i] = (pageRotations[i] + angle) % 360;
        }
        setPageRotations(newRotations);
        toast.info(`All pages rotated ${angle}°`);
    };

    const resetRotations = () => {
        const resetRotations = {};
        for (let i = 0; i < totalPages; i++) {
            resetRotations[i] = 0;
        }
        setPageRotations(resetRotations);
        toast.info('All rotations reset');
    };

    const applyRotations = async () => {
        if (!pdfDoc) return;

        try {
            setIsProcessing(true);
            setProgress(10);

            // Create a new PDF document
            const newPdf = await PDFDocument.create();
            setProgress(30);

            // Copy and rotate each page
            for (let i = 0; i < totalPages; i++) {
                setProgress(30 + (i / totalPages) * 60);

                const [copiedPage] = await newPdf.copyPages(pdfDoc, [i]);

                // Apply rotation
                const rotation = pageRotations[i];
                if (rotation !== 0) {
                    copiedPage.setRotation(degrees(rotation));
                }

                newPdf.addPage(copiedPage);
            }

            setProgress(95);

            // Save the PDF
            const pdfBytes = await newPdf.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });

            setProgress(100);

            // Set preview
            const filename = `rotated-${file.name}`;
            setPreviewBlob(blob);
            setPreviewFilename(filename);

            toast.success('PDF rotated! Opening preview...');

            setTimeout(() => {
                setProgress(0);
            }, 1000);

        } catch (error) {
            console.error('Rotation error:', error);
            toast.error(`Failed to rotate PDF: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    // Generate live preview when rotations change
    const generateLivePreview = async () => {
        if (!pdfDoc || totalPages === 0) {
            setPreviewBlob(null);
            return;
        }

        try {
            const newPdf = await PDFDocument.create();

            // Copy and rotate each page
            for (let i = 0; i < totalPages; i++) {
                const [copiedPage] = await newPdf.copyPages(pdfDoc, [i]);
                const rotation = pageRotations[i] || 0;
                if (rotation !== 0) {
                    copiedPage.setRotation(degrees(rotation));
                }
                newPdf.addPage(copiedPage);
            }

            const pdfBytes = await newPdf.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            const filename = `rotated-${file.name}`;
            
            setPreviewBlob(blob);
            setPreviewFilename(filename);
        } catch (error) {
            console.error('Preview generation error:', error);
            setPreviewBlob(null);
        }
    };

    // Auto-update preview when rotations change
    useEffect(() => {
        if (file && pdfDoc && totalPages > 0) {
            const timeoutId = setTimeout(() => {
                generateLivePreview();
            }, 300);
            return () => clearTimeout(timeoutId);
        }
    }, [pageRotations, file, pdfDoc, totalPages]);

    return (
        <div>
            {/* Action Buttons */}
            <ToolActionButtons
                onAdd={() => {
                    document.querySelector('input[type="file"]')?.click();
                }}
                notificationCount={file ? 1 : 0}
            />

            {/* Header */}
            <div className="tool-header">
                <h1 className="tool-title">
                    <RotateCw size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    Rotate PDF Pages
                </h1>
                <p className="tool-description">
                    Rotate individual pages or all pages in your PDF document. Choose 90°, 180°, or 270° rotation.
                </p>
            </div>

            {/* File Upload */}
            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here to rotate"
                    description="or click to browse"
                />
            ) : (
                <Alert variant="success" title="PDF Loaded">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileText size={16} />
                            <span>{file.name}</span>
                            <Badge variant="info">{totalPages} pages</Badge>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setFile(null);
                                setPdfDoc(null);
                                setTotalPages(0);
                                setPageRotations({});
                            }}
                        >
                            Change PDF
                        </Button>
                    </div>
                </Alert>
            )}

            {file && (
                <>
                    {/* Global Rotation Controls */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{
                                fontSize: 'var(--text-xl)',
                                fontWeight: 'var(--font-bold)',
                                marginBottom: 'var(--spacing-4)'
                            }}>
                                Rotate All Pages
                            </h3>

                            <div style={{ display: 'flex', gap: 'var(--spacing-3)', flexWrap: 'wrap' }}>
                                <Button variant="secondary" onClick={() => rotateAllPages(90)}>
                                    <RotateCw size={18} />
                                    Rotate All 90° →
                                </Button>
                                <Button variant="secondary" onClick={() => rotateAllPages(180)}>
                                    <RotateCw size={18} />
                                    Rotate All 180° ↓
                                </Button>
                                <Button variant="secondary" onClick={() => rotateAllPages(270)}>
                                    <RotateCw size={18} />
                                    Rotate All 270° ←
                                </Button>
                                <Button variant="ghost" onClick={resetRotations}>
                                    Reset All
                                </Button>
                            </div>
                        </Card>
                    </div>

                    {/* Individual Page Controls */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{
                                fontSize: 'var(--text-xl)',
                                fontWeight: 'var(--font-bold)',
                                marginBottom: 'var(--spacing-4)'
                            }}>
                                Rotate Individual Pages
                            </h3>

                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                gap: 'var(--spacing-4)'
                            }}>
                                {Array.from({ length: totalPages }, (_, i) => (
                                    <Card key={i} variant="outlined">
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{
                                                width: '100%',
                                                aspectRatio: '1',
                                                backgroundColor: 'var(--color-surface-elevated)',
                                                borderRadius: 'var(--radius-md)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginBottom: 'var(--spacing-3)',
                                                position: 'relative',
                                                overflow: 'hidden'
                                            }}>
                                                <div style={{
                                                    transform: `rotate(${pageRotations[i]}deg)`,
                                                    transition: 'transform var(--transition-base)',
                                                    fontSize: 'var(--text-4xl)',
                                                    fontWeight: 'var(--font-bold)',
                                                    color: 'var(--color-text-tertiary)'
                                                }}>
                                                    {i + 1}
                                                </div>
                                                {pageRotations[i] !== 0 && (
                                                    <Badge
                                                        variant="warning"
                                                        style={{
                                                            position: 'absolute',
                                                            top: 'var(--spacing-2)',
                                                            right: 'var(--spacing-2)'
                                                        }}
                                                    >
                                                        {pageRotations[i]}°
                                                    </Badge>
                                                )}
                                            </div>

                                            <div style={{
                                                fontSize: 'var(--text-sm)',
                                                fontWeight: 'var(--font-medium)',
                                                marginBottom: 'var(--spacing-2)'
                                            }}>
                                                Page {i + 1}
                                            </div>

                                            <div style={{ display: 'flex', gap: 'var(--spacing-1)', justifyContent: 'center' }}>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    icon
                                                    onClick={() => rotatePage(i, 90)}
                                                    title="Rotate 90°"
                                                >
                                                    <RotateCw size={14} />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    icon
                                                    onClick={() => rotatePage(i, 180)}
                                                    title="Rotate 180°"
                                                >
                                                    180°
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    icon
                                                    onClick={() => rotatePage(i, 270)}
                                                    title="Rotate 270°"
                                                >
                                                    270°
                                                </Button>
                                            </div>
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        </Card>
                    </div>

                    {/* Progress Bar */}
                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <ProgressBar value={progress} striped showLabel />
                        </div>
                    )}

                    {/* Apply Button */}
                    <div style={{ marginTop: 'var(--spacing-6)', display: 'flex', gap: 'var(--spacing-3)' }}>
                        {previewBlob && (
                            <Button
                                variant="secondary"
                                size="lg"
                                onClick={() => setShowPreview(true)}
                                style={{ flex: 1 }}
                            >
                                <FileText size={20} style={{ marginRight: 'var(--spacing-2)' }} />
                                View Preview
                            </Button>
                        )}
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={applyRotations}
                            disabled={isProcessing}
                            loading={isProcessing}
                            style={{ flex: previewBlob ? 1 : 'none', width: previewBlob ? 'auto' : '100%' }}
                            fullWidth={!previewBlob}
                        >
                            <Download size={20} />
                            Apply Rotations & Download
                        </Button>
                    </div>
                </>
            )}

            {/* PDF Preview Modal */}
            {previewBlob && showPreview && (
                <PDFPreview
                    pdfBlob={previewBlob}
                    filename={previewFilename}
                    onClose={() => setShowPreview(false)}
                    onDownload={() => {
                        if (previewBlob && previewFilename) {
                            saveAs(previewBlob, previewFilename);
                            toast.success('Download started!');
                        }
                    }}
                />
            )}
        </div>
    );
};

export default RotatePDF;
